/**
 * SKŁADNIA createAsyncThunk:
 * https://redux-toolkit.js.org/api/createAsyncThunk
 */


import {  UnknownAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { createAsyncThunk } from '@reduxjs/toolkit'
import { AppState } from "store"
import { SLICE_KEYS } from 'store/SliceKeys'
import { clear, kontomatikBankVerification, setIsAdditionalVerificationMethodsAvailable } from '../ApplicationSlice'
import { HttpClient, StartLinkVisitParams } from 'common/HttpClient'
import { STORAGEKEYS } from 'enviroment/Keys'
import { Guid, isTrastiBroker } from 'common/helpers/helpers'
import { OffertCamelCase } from 'models/responses/AuthSuccesfullResponse'
import { Environment } from 'enviroment/AppSettings'
import { SessionStorage } from 'common/CookieStorage'
import { HubClient } from 'common/HubClient'
import { ConnectionState, IDocumentsRedyHub, IGetCredentialsThunkActionReturnProps, IRejectedHub } from './credential-actions-models'
import { AppLinks } from 'enviroment/AppLinks'
import { hubConnected, hubDisconnected, hubUserLogged } from 'store/HubSlice/HubSlice'
import settingsService from 'service/SettingsService/SettingsService'
import { appConsentsFetched, consentsFetched } from 'store/ConsentsSlice/ConsentsSlcie'
import apphistory from 'common/AppHistory'
import { IKontomatikBankVerificationAction } from '../ApplicationSliceModels'
import { setAgreementFileName } from 'store/ContractSignSlice/ContractSignSlice'
import { setFieldValue } from 'store/FormSlice/FormSlice'
import { BasicInfoFieldsNames } from 'FormDefinitions/BasicInfoForm/BasicInfoFields'
import { WorkflowTypes } from 'common/workflow'
import { ConsentType } from 'service/SettingsService/SettingsServiceModels'
import analyticsService from 'service/AnalyticsService/AnalyticsService'
import { IAnalyticsEventsTypesNamesResponse } from 'service/AnalyticsService/AnalyticsServiceModels'


const _clearMObywatelCache = (sessionStorage: SessionStorage) => {
  sessionStorage.changeItemValue(STORAGEKEYS.CURRENT_MOBYWATEL_CODE, "")
  sessionStorage.changeItemValue(STORAGEKEYS.MOBYWATEL_CODE_VALIDITY_STARTING_TIME, "0")
  sessionStorage.changeItemValue(STORAGEKEYS.MOBYWATEL_IS_AFTER_FIRST_RENDER, "false")
}

export type GetCredentialsArgs = string | {
  orderId: string
  isStartLink?: boolean
  source?: string | null
}

const resolveGetCredentialsArgs = (args: GetCredentialsArgs): { orderId: string, startLinkVisit?: StartLinkVisitParams } => {
  if (typeof args === "string") {
    return { orderId: args }
  }

  return {
    orderId: args.orderId,
    startLinkVisit: args.isStartLink
      ? { isStartLink: true, source: args.source }
      : undefined
  }
}

export const getCredentials = createAsyncThunk(
   `${SLICE_KEYS.APPLICATION_SLICE}/getCredentials`,
   async(args: GetCredentialsArgs, thunkAPI): Promise<IGetCredentialsThunkActionReturnProps | undefined> => {
      const { orderId, startLinkVisit } = resolveGetCredentialsArgs(args)
      thunkAPI.dispatch(clear())

      const client = new HttpClient(Environment.apiUrl)
      const response = (await client.authRequest(orderId, startLinkVisit)).data

      if (response) {
        const sessionStorage = new SessionStorage()

        sessionStorage.add(STORAGEKEYS.ACCESS_TOKEN, response.access_token)
        sessionStorage.add(STORAGEKEYS.USER_SESSION, "true")
        sessionStorage.addCookie(STORAGEKEYS.SESSION_ID, Guid())
        sessionStorage.add(STORAGEKEYS.SPECIAL_HANDLING_BROKER_NAME, response.specialHandlingBrokerName || "")
    
        const consents = await settingsService.getConsents(WorkflowTypes.IsRetention(response.workflowId)? ConsentType.Retention : ConsentType.Standard)
        thunkAPI.dispatch(consentsFetched(consents))
        thunkAPI.dispatch(appConsentsFetched(consents))
        thunkAPI.dispatch(hubUserLogged())
        thunkAPI.dispatch(setFieldValue({ fieldName: BasicInfoFieldsNames.Email, newValue: response.email })) // Przepisujemy adres email użytkownika który dostaniemy z API do formularza

        analyticsService.sendBrowserData() // Wysyłanie danych statystycznych do backendu
         
        const  offert = OffertCamelCase(response.offert)
         
        return {
          offert: offert,
          billingCompany: response.biillingCompany,
          appId: orderId,
          amountToPay: response.requestedAmount,
          successReturnUrl: response.successReturnUrl,
          failureReturnUrl: response.failureReturnUrl,
          applicationStatus: response.currentStatus,
          workflowId: response.workflowId,
          hashedPhoneNumber: response.hashedPhoneNumber,
          maxLoanPeriod: response.maxLoanPeriod,
        }
      }
      else {
        return undefined
      }
   }
)

const subscribeToHub = (hub: HubClient, dispatch: ThunkDispatch<unknown, unknown, UnknownAction>, analyticsEventTypesNames?: IAnalyticsEventsTypesNamesResponse) => {
  const hubMethods = HubClient.hubMethods
  const sessionStorage = new SessionStorage()

    // ================= APPLICATION REJECTED =================
  hub.subscribe(hubMethods.applicationRejected, (data: IRejectedHub) => {
    apphistory.push(AppLinks.fail)
  });


  // ================= BANK VERIFICATION =================
  hub.subscribe(hubMethods.bmVerifyUrl, (data: IKontomatikBankVerificationAction) => {
    dispatch(kontomatikBankVerification(data))
    dispatch(setIsAdditionalVerificationMethodsAvailable(data.mobVerificacationAvaiable))
    _clearMObywatelCache(sessionStorage)
    
    //TODO Tekst na kontomatiku zależny od `mobVerificacationAvaiable`

    if(isTrastiBroker()){
      apphistory.push(AppLinks.productPage)
    }
    else {
      apphistory.push(AppLinks.bmverify)
    }
  });


  // ================= CONTRACT SIGNING =================
  hub.subscribe(hubMethods.documentsRedy, (data: IDocumentsRedyHub) => {
    dispatch(setAgreementFileName(data.fileToDownloadName))
    
    apphistory.push(AppLinks.contractSigning)
  });


  // ================= SESSION TIMEOUT =================
  hub.subscribe(hubMethods.sessionTimeout, () => {
    apphistory.push(AppLinks.sessionEnd)
  });


  // ================= MOBYWATEL FORM =================
  hub.subscribe(hubMethods.mObywatelIdentification, () => {
    // Czyszczenie cache z MObywatela
    sessionStorage.add(STORAGEKEYS.IS_MOBYWATEL_IDENTIFICATION_SUCCEED, 'true')
    _clearMObywatelCache(sessionStorage)
    
    analyticsEventTypesNames !== undefined && analyticsService.sendAnalyticEvent(analyticsEventTypesNames.MobywatelSuccess)

    apphistory.push(AppLinks.mObywatelExtendedInfoForm)
  });

  // ================= MOBYWATEL BANK VERIFICATION =================
  hub.subscribe(hubMethods.mObywatelVerification, (data: IDocumentsRedyHub) => { // Po pomyślnej weryfikacji danych w mObywatelu, musimy dostać nazwę pliku z Backendu, tak jak w przypadku ścieżki z weryfikacją w Kontomatiku
    // Czyszczenie cache z MObywatela
    sessionStorage.add(STORAGEKEYS.IS_MOBYWATEL_VERIFICATION_SUCCEED, 'true')
    _clearMObywatelCache(sessionStorage)
    dispatch(setAgreementFileName(data.fileToDownloadName))

    apphistory.push(AppLinks.contractSigning)
  });
}

export const tryReconnectToHub = createAsyncThunk(
  `${SLICE_KEYS.APPLICATION_SLICE}/tryReconnectToHub`,
  
  async( _, { getState, dispatch }) => {
    const cookiesStorage = new SessionStorage();
    const sessionState = cookiesStorage.get(STORAGEKEYS.USER_SESSION);
    const state = getState() as AppState
    
    if (!state.hubState.isConnected && sessionState !== null) {
      const client = new HttpClient(Environment.apiUrl)
      const result = (await client.get<ConnectionState>("connection", true)).data

      if (!result.isFinished) {
        const hub = new HubClient("hub")
        subscribeToHub(hub, dispatch, state.globalConfigurationState.analyticsEventTypesNames) 
        hub.start()

        dispatch(hubConnected())
      }
    }  
  }
)

export const reconnectOnInvalidConnection = createAsyncThunk(
  `${SLICE_KEYS.APPLICATION_SLICE}/reconnectOnInvalidConnection`,

  async( _, { getState, dispatch }) => {

    const cookiesStorage = new SessionStorage()
    const sessionState = cookiesStorage.get(STORAGEKEYS.USER_SESSION)
    const state = getState() as AppState
    
    if (!state.applicationState.isLoading  && sessionState !== null) {
      const client = new HttpClient(Environment.apiUrl)
      const result = (await client.get<ConnectionState>("connection", true)).data
      
      const storage = new SessionStorage();
      const sessionState = storage.get(STORAGEKEYS.USER_SESSION);

      if(!result.isFinished) {
        if ((!result.isConnected || !result.isValid) && sessionState !== undefined){
          const hub = new HubClient("hub")
          subscribeToHub(hub, dispatch, state.globalConfigurationState.analyticsEventTypesNames)
          hub.start();

          dispatch(hubConnected())
        }
        else {
          dispatch(hubDisconnected())
        }
      }
    }  
  }
)